import { useState } from 'react';
import { chipColors, onLight } from '../../lib/contrast';
import { Chip, Avatar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SttUnlockerDialog } from '../all';

type UnlockerType =
    | 'PIN'
    | 'QR'
    | 'CARD'
    | 'TICKET_CARD'
    | 'FINGERPRINT'
    | 'TICKET_ONE_USE';

type Unlocker = {
    id: string | number;
    type: UnlockerType;
};

type TypeConfig = {
    letter: string;
    color: string;
    textColor: string;
};

type Props = {
    unlocker?: Unlocker | null;
    showModal?: boolean;
    onClick?: (() => void) | null;
};

/**
 * Type configuration with colors
 */
const typeConfig: Record<UnlockerType, TypeConfig> = {
    PIN: { letter: 'P', color: '#4CAF50', textColor: 'white' }, // Green
    QR: { letter: 'Q', color: '#2196F3', textColor: 'white' }, // Blue
    CARD: { letter: 'C', color: '#FF9800', textColor: 'white' }, // Orange
    TICKET_CARD: { letter: 'T', color: '#9C27B0', textColor: 'white' }, // Purple
    FINGERPRINT: { letter: 'F', color: '#E91E63', textColor: 'white' }, // Pink
    TICKET_ONE_USE: { letter: '1', color: '#607D8B', textColor: 'white' }, // Blue Grey
};

const defaultTypeConfig: TypeConfig = {
    letter: 'X',
    color: '#9E9E9E',
    textColor: 'white',
};

/**
 * Chip unlocker
 */
export function SttChipUnlocker({
    unlocker = null,
    showModal = false,
    onClick = null,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const [unlockerDialogOpened, setUnlockerDialogOpened] =
        useState<boolean>(false);
    const [isDeleted, setIsDeleted] = useState<boolean>(false);

    const config = unlocker?.type
        ? typeConfig[unlocker.type] || defaultTypeConfig
        : defaultTypeConfig;

    // Render
    return (
        <>
            {unlocker && unlocker.type && !isDeleted && (
                <Chip
                    size="small"
                    style={{
                        marginRight: 3,
                        marginBottom: 3,
                        ...chipColors(config.color),
                    }}
                    avatar={
                        <Avatar
                            style={{
                                backgroundColor: 'rgba(255,255,255,0.95)',
                                color: onLight(config.color),
                                fontWeight: 600,
                            }}
                            alt={t(`UT.${unlocker.type}`)}
                        >
                            {config.letter}
                        </Avatar>
                    }
                    label={t(`UT.${unlocker.type}`)}
                    onClick={
                        showModal
                            ? () => setUnlockerDialogOpened(true)
                            : onClick || undefined
                    }
                />
            )}

            {showModal && unlocker && unlockerDialogOpened && (
                <SttUnlockerDialog
                    idUnlocker={
                        typeof unlocker.id === 'number'
                            ? unlocker.id
                            : undefined
                    }
                    open={unlockerDialogOpened}
                    onClose={() => setUnlockerDialogOpened(false)}
                    onDelete={() => setIsDeleted(true)}
                />
            )}
        </>
    );
}
